import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, MapPinIcon, CurrencyRupeeIcon, CheckBadgeIcon, ArrowTopRightOnSquareIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { User } from '../../types';
import Button from '../common/Button';
import Avatar from '../common/Avatar';
import { resolveImageUrl } from '../../utils/image';

interface ModelQuickViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onBook: () => void;
  onViewProfile: () => void;
}

const ModelQuickViewModal: React.FC<ModelQuickViewModalProps> = ({
  isOpen,
  onClose,
  user,
  onBook,
  onViewProfile
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(null);

  if (!isOpen) return null;

  const mp = user.model_profile;
  const heightStr = mp?.height
    ? `${mp.height} cm (${Math.floor((mp.height / 2.54) / 12)}'${Math.round((mp.height / 2.54) % 12)}")`
    : '—';
  const weightStr = mp?.weight
    ? `${mp.weight} kg (${Math.round(mp.weight * 2.20462)} lbs)`
    : '—';
  const bustStr = mp?.bust ? `${mp.bust}` : '—';
  const waistStr = mp?.waist ? `${mp.waist}` : '—';
  const hipsStr = mp?.hips ? `${mp.hips}` : '—';

  // Get portfolio previews or digitals
  const previewImages = mp?.digitals && mp.digitals.length > 0
    ? mp.digitals.slice(0, 4)
    : (user.recent_images_signed || user.recent_images || []).slice(0, 4);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white dark:bg-gray-900 border border-gray-150 dark:border-gray-800 rounded-3xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
            <span className="text-xs font-bold text-teal-600 dark:text-teal-400 uppercase tracking-widest">Model Quick View</span>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* Header info */}
            <div className="flex gap-4">
              <Avatar
                src={user.profile_pic_signed || resolveImageUrl(user.profile_pic)}
                alt={user.first_name}
                size="lg"
                className="w-16 h-16 border-2 border-teal-500/20"
              />
              <div className="space-y-1">
                <h3 className="text-lg font-extrabold text-gray-950 dark:text-white flex items-center gap-1.5">
                  {user.first_name} {user.last_name}
                  {user.is_verified && <CheckBadgeIcon className="w-5 h-5 text-teal-500" />}
                </h3>
                {user.location && (
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    <MapPinIcon className="w-3.5 h-3.5" />
                    <span>{user.location}</span>
                  </div>
                )}
                {mp?.agency_represented && (
                  <span className="inline-block text-[10px] font-bold text-teal-700 bg-teal-50 dark:text-teal-400 dark:bg-teal-900/20 px-2 py-0.5 rounded-md border border-teal-100 dark:border-teal-900/30">
                    {mp.agency_name}
                  </span>
                )}
              </div>
            </div>

            {/* Bio */}
            {user.bio && (
              <div className="bg-gray-50 dark:bg-gray-800/30 p-4 rounded-2xl text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                {user.bio}
              </div>
            )}

            {/* Specifications Card */}
            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-800 p-4 rounded-2xl space-y-3 shadow-sm">
              <span className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest block">Physical specs</span>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs font-semibold text-gray-600 dark:text-gray-400">
                <div>Height:</div>
                <div className="text-gray-950 dark:text-white text-right">{heightStr}</div>
                
                <div>Weight:</div>
                <div className="text-gray-950 dark:text-white text-right">{weightStr}</div>

                <div>Measurements (B-W-H):</div>
                <div className="text-gray-950 dark:text-white text-right">
                  {bustStr}-{waistStr}-{hipsStr} cm
                </div>

                <div>Shoe / Dress Size:</div>
                <div className="text-gray-950 dark:text-white text-right">
                  {mp?.shoe_size || '—'} / {mp?.dress_size || '—'}
                </div>

                <div>Hair / Eye Color:</div>
                <div className="text-gray-950 dark:text-white text-right">
                  {mp?.hair_color || '—'} / {mp?.eye_color || '—'}
                </div>
              </div>
            </div>

            {/* Rates & Booking Terms */}
            {mp && (mp.half_day_rate || mp.full_day_rate) && (
              <div className="bg-gray-50 dark:bg-gray-800/40 p-4 rounded-2xl space-y-3">
                <span className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest block">Estimated Booking Rates</span>
                <div className="grid grid-cols-2 gap-y-2 text-xs font-semibold text-gray-600 dark:text-gray-400">
                  {mp.half_day_rate && (
                    <>
                      <div>Half Day (4h):</div>
                      <div className="text-gray-950 dark:text-white font-bold text-right">₹{mp.half_day_rate.toLocaleString()}</div>
                    </>
                  )}
                  {mp.full_day_rate && (
                    <>
                      <div>Full Day (8h):</div>
                      <div className="text-gray-950 dark:text-white font-bold text-right">₹{mp.full_day_rate.toLocaleString()}</div>
                    </>
                  )}
                  <div>Shoot Preferences:</div>
                  <div className="text-gray-950 dark:text-white text-right truncate">
                    {[
                      mp.accepts_paid !== false ? 'Paid' : null,
                      mp.accepts_tfp !== false ? 'TFP' : null
                    ].filter(Boolean).join(' / ') || '—'}
                  </div>
                </div>
              </div>
            )}

            {/* Previews / Digitals */}
            {previewImages.length > 0 && (
              <div className="space-y-3">
                <span className="text-[10px] font-extrabold text-gray-400 dark:text-gray-500 uppercase tracking-widest block">Featured Looks / Digitals</span>
                <div className="grid grid-cols-4 gap-2">
                  {previewImages.map((url, idx) => (
                    <div 
                      key={idx} 
                      className="aspect-[3/4] rounded-xl overflow-hidden bg-gray-150 border dark:border-gray-800 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedImageIndex(idx)}
                    >
                      <img src={resolveImageUrl(url)} alt={`Look preview ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="p-5 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 flex justify-end gap-2.5 rounded-b-3xl">
            <Button variant="outline" size="sm" onClick={onViewProfile} className="flex items-center gap-1">
              View Profile
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" onClick={onBook}>
              Book Model
            </Button>
          </div>
        </motion.div>

        {/* Fullscreen Image Preview Lightbox */}
        <AnimatePresence>
          {selectedImageIndex !== null && (
            <div 
              className="fixed inset-0 z-[90] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md select-none cursor-default"
              onClick={() => setSelectedImageIndex(null)}
            >
              {/* Close button */}
              <button
                onClick={() => setSelectedImageIndex(null)}
                className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-50 shadow-md"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              {/* Left navigation arrow */}
              {selectedImageIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(prev => prev !== null ? prev - 1 : null);
                  }}
                  className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-50 shadow-md"
                >
                  <ChevronLeftIcon className="w-6 h-6" />
                </button>
              )}

              {/* Right navigation arrow */}
              {selectedImageIndex < previewImages.length - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex(prev => prev !== null ? prev + 1 : null);
                  }}
                  className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-50 shadow-md"
                >
                  <ChevronRightIcon className="w-6 h-6" />
                </button>
              )}

              {/* Active image container */}
              <motion.div
                key={selectedImageIndex}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative max-w-full max-h-[85vh] flex flex-col items-center gap-4"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={resolveImageUrl(previewImages[selectedImageIndex])}
                  alt={`Preview Look ${selectedImageIndex + 1}`}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl border border-white/5"
                />
                <div className="text-center text-white/75 text-xs font-black uppercase tracking-widest mt-2">
                  Look {selectedImageIndex + 1} of {previewImages.length}
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </AnimatePresence>
  );
};

export default ModelQuickViewModal;
